import { IncidentStatus, IncidentSummary } from '../api/incidentLog';
import { realtimeKindLabel } from './realtimeKindLabels';

/**
 * How each derived status reads and is coloured — one table for the map
 * (hull/marker colours), the list (chips) and the popover/modal. The
 * backend decides the status (api/services/notifications/status.py); this
 * only presents it.
 */
export const INCIDENT_STATUS_META: Record<
    IncidentStatus,
    { label: string; color: string; description: string; closed: boolean }
> = {
    escalated_rekindle: {
        label: 'Escalated rekindle',
        color: '#ef4444',
        description:
            'A re-detection at a closed fire that has spread beyond its footprint or drawn an agency warning. Treated as news, like a new fire.',
        closed: false,
    },
    active: {
        label: 'Active',
        color: '#f59e0b',
        description: 'Open, with detections in the last 24 hours.',
        closed: false,
    },
    silent_rekindle: {
        label: 'Silent rekindle',
        color: '#94a3b8',
        description:
            "Re-detections inside a closed fire's footprint — residual heat until proven otherwise. Nobody notified, no agent.",
        closed: false,
    },
    stand_down: {
        label: 'Stand-down',
        color: '#64748b',
        description:
            "A silent rekindle under a commander's stand-down: geometry cannot escalate it until the stand-down ends.",
        closed: false,
    },
    quiet: {
        label: 'Quiet',
        color: '#a1a1aa',
        description: 'Open, but nothing detected for 24 hours.',
        closed: false,
    },
    closed_by_human: {
        label: 'Closed',
        color: '#6b7280',
        description: 'A person declared this incident over.',
        closed: true,
    },
    closed_by_agent: {
        label: 'Closed by agent',
        color: '#6b7280',
        description: 'The AI agent judged this incident over.',
        closed: true,
    },
};

/** Statuses grouped the way a reader triages them. */
export const INCIDENT_STATUS_GROUPS: {
    key: 'attention' | 'watching' | 'closed';
    label: string;
    statuses: IncidentStatus[];
}[] = [
    { key: 'attention', label: 'Needs attention', statuses: ['escalated_rekindle', 'active'] },
    { key: 'watching', label: 'Watching', statuses: ['silent_rekindle', 'stand_down', 'quiet'] },
    { key: 'closed', label: 'Closed', statuses: ['closed_by_human', 'closed_by_agent'] },
];

export function statusLabel(status: IncidentStatus): string {
    return INCIDENT_STATUS_META[status]?.label ?? status;
}

export function statusColor(status: IncidentStatus): string {
    return INCIDENT_STATUS_META[status]?.color ?? '#94a3b8';
}

/**
 * Display title for an incident. Announced incidents carry their digest
 * title; a silent rekindle never gets one (nobody was told), so name it by
 * what it is rather than the bare kind label.
 */
export function incidentTitle(incident: IncidentSummary): string {
    if (incident.title) return incident.title;
    const kind = realtimeKindLabel(incident.kind);
    if (incident.predecessor_id !== null) {
        return `Rekindle of #${incident.predecessor_id}`;
    }
    if (incident.primary_id !== null && incident.primary_project) {
        return `Fire tracked by ${incident.primary_project.name} (#${incident.primary_id})`;
    }
    return `${kind} event`;
}

/** Kinds absorbed beyond the opening one, e.g. a hotspot incident that also
 *  received burnt-area perimeters. */
export function extraKinds(incident: IncidentSummary): string[] {
    return (incident.kinds ?? [incident.kind]).filter((k) => k !== incident.kind);
}

/**
 * How this incident relates to the same fire on neighbouring projects, or
 * null when it stands alone.
 */
export function linkedIncidentSummary(incident: IncidentSummary): string | null {
    if (incident.primary_id !== null) {
        const name = incident.primary_project?.name ?? 'a neighbouring project';
        return (
            `Linked to incident #${incident.primary_id} on ${name}, which first tracked this ` +
            'fire and holds its full footprint. One agent (if any) monitors the fire for ' +
            'every linked project; its notifications reach subscribers here too.'
        );
    }
    const secondaries = incident.secondaries ?? [];
    if (!secondaries.length) return null;
    const list = secondaries.map((s) => `#${s.id} on ${s.project_name}`).join(', ');
    return `This fire is also within reach of ${secondaries.length === 1 ? 'a neighbouring project' : 'neighbouring projects'}: ${list}. Those incidents link to this one.`;
}

export function formatWhen(iso: string): string {
    return new Date(iso).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export function formatMetres(m: number): string {
    return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}

function plural(n: number, noun: string): string {
    return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/**
 * Why this rekindle is (or is not) being handled. The backend decides
 * silent vs escalated from deterministic geometry; this just explains it.
 */
export function rekindleExplanation(incident: IncidentSummary): string {
    const r = incident.rekindle;
    const chain = incident.chain?.length ? incident.chain : [incident.predecessor_id];
    const chainText =
        chain.length > 1
            ? `incident #${chain[0]} (itself a rekindle of ${chain
                  .slice(1)
                  .map((id) => `#${id}`)
                  .join(', ')})`
            : `incident #${chain[0]}`;
    const linked =
        incident.link_distance_m !== null && incident.link_distance_m !== undefined
            ? ` Linked because its nearest opening detection was ${formatMetres(
                  incident.link_distance_m
              )} from that fire's detection footprint${
                  incident.link_radius_m
                      ? ` (within the ${formatMetres(incident.link_radius_m)} link radius)`
                      : ''
              }.`
            : '';
    const lead = `Re-detection at ${chainText}, which had been closed.${linked}`;
    if (!r) return lead;
    if (incident.silent) {
        const parts = [
            lead,
            `All ${plural(r.inside_prior_footprint, 'detection')} so far sit inside that fire's ` +
                'satellite detection footprint (residual heat at a contained fire), so nobody ' +
                'was notified and no agent was started.',
        ];
        if (incident.stand_down_until && new Date(incident.stand_down_until) > new Date()) {
            parts.push(
                `A commander stand-down is in force until ${formatWhen(
                    incident.stand_down_until
                )}: only a Watch and Act or Emergency Warning escalates before then.`
            );
        } else if (r.outside_prior_footprint > 0) {
            parts.push(
                `${plural(r.outside_prior_footprint, 'detection')} landed up to ` +
                    `${formatMetres(r.max_outside_m ?? 0)} beyond the footprint on a single ` +
                    'pass. A human closed the original incident, so a second distinct pass ' +
                    'beyond the footprint is needed before it escalates.'
            );
        } else {
            parts.push(
                'It escalates automatically if a detection lands beyond that footprint or an ' +
                    'agency warning is issued for the area.'
            );
        }
        return parts.join(' ');
    }
    const reason =
        r.escalation_reason === 'agency_warning'
            ? 'an agency warning was issued for the area'
            : `${plural(r.outside_prior_footprint, 'detection')} landed up to ${formatMetres(
                  r.max_outside_m ?? 0
              )} beyond that fire's satellite detection footprint`;
    return (
        `${lead} Escalated${r.escalated_at ? ` ${formatWhen(r.escalated_at)}` : ''}: ` +
        `${reason}, so it was treated as news — subscribers announced and, on ` +
        'auto-monitored projects, an agent started with the closed incident\'s history.'
    );
}
